import { SweepNotificationVariant } from '../../../reducers/notificationsReducer';
import { useSweepNotifications } from '../../notifications/useSweepNotifications';
import { ConversationStatus, AiAssistantConversation } from './types';
import { useAiSupportApiFacade } from '../../../apis/facades/useAiSupportApiFacade';
import { useCallback, useState } from 'react';
import {
  markConversationAsResolved,
  setAiAssistantConversations,
  setApps,
} from '../../../reducers/aiSupportReducer';
import { useDispatch, useSelector } from 'react-redux';
import { selectApps } from '../../../reducers/aiSupportReducer';
import { selectDefaultCreationCrmOrgId } from '../../../reducers/userInfoReducer';
import { SfAppElement } from './apps/AppsConnectionDialog';

export const useAiAssistant = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { addNotification } = useSweepNotifications();
  const { put_mark_as_resolved, get_supported_apps, get_ai_sessions } = useAiSupportApiFacade();
  const dispatch = useDispatch();

  const defaultCrmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const apps = useSelector(selectApps(defaultCrmOrgId ?? ''));
  const isInitialized = apps?.filter((el: SfAppElement) => el.hasSweepSupport)?.length > 0;

  const fetchApps = useCallback(
    async (defaultCrmOrgId: string, withLoading?: boolean) => {
      setIsLoading(!!withLoading);
      try {
        const appsResponse = await get_supported_apps();
        dispatch(setApps({ crmOrgId: defaultCrmOrgId, apps: appsResponse }));
        withLoading && setIsLoading(false);
        return appsResponse;
      } catch {
        dispatch(setApps({ crmOrgId: defaultCrmOrgId, apps: [] }));
        withLoading && setIsLoading(false);

        return [];
      }
    },
    [get_supported_apps, dispatch],
  );

  const fetchAiAssistentConversations = useCallback(
    async (defaultCrmOrgId: string) => {
      setIsLoading(true);
      try {
        const response = await get_ai_sessions();
        const _data = response;
        const sortedByDate = _data?.sort((a: any, b: any) => {
          return new Date(b.updatedAt).valueOf() - new Date(a.updatedAt).valueOf();
        });
        dispatch(
          setAiAssistantConversations({
            aiAssistantConversations: sortedByDate,
            crmOrgId: defaultCrmOrgId,
          }),
        );
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error(error);
      }
    },
    [dispatch, get_ai_sessions],
  );

  const markAsResolved = useCallback(
    async (itemId: AiAssistantConversation, defaultCrmOrgId: string) => {
      const _session = itemId.messages?.[0]?.sessionId;
      if (_session) {
        dispatch(
          markConversationAsResolved({
            conversationId: itemId.id,
            status: ConversationStatus.Closed_By_Admin,
            crmOrgId: defaultCrmOrgId,
          }),
        );
        await put_mark_as_resolved(_session, 'Closed_By_Admin');
        addNotification({
          message: 'Conversation marked as resolved',
          variant: SweepNotificationVariant.Success,
        });
      }
    },
    [put_mark_as_resolved, addNotification, dispatch],
  );

  return {
    markAsResolved,
    fetchAiAssistentConversations,
    isLoading,
    isInitialized,
    fetchApps,
  };
};
